import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { map } from 'rxjs';
import { environment } from '../../environments/environment';
import { ApiEnvelope } from './payment';

// ===================== models =====================

/** A partner mobile-app user — the push target options. */
export interface PartnerAppUser {
  userId: number;
  username: string;
  partnerName: string;
  partnerCode: string;
}

/** Write model for POST /Push. `userId` null = broadcast to ALL partners. */
export interface PushNotificationDto {
  userId: number | null;
  title: string;
  body: string;
}

/** Sent-history row from GET /Pushes. `userId` null = it was a broadcast. */
export interface PushRow {
  id: number;
  title: string;
  body: string;
  createdAt: string | null; // UTC ISO
  userId: number | null;
  targetUsername: string | null;
  readCount: number;
}

export type IssueStatus = 'OPEN' | 'REPLIED' | 'CLOSED';

/** Issues-list row from GET /Issues. */
export interface IssueRow {
  id: number;
  title: string;
  body: string;
  status: IssueStatus | string;
  createdAt: string | null; // UTC ISO
  isReadByAdmin: boolean;
  userId: number;
  username: string;
  partnerName: string;
  partnerCode: string;
  replyCount: number;
}

/** `issue` of GET /IssueThread — the list row plus the partner's contact details. */
export interface IssueDetail {
  id: number;
  title: string;
  body: string;
  status: IssueStatus | string;
  createdAt: string | null; // UTC ISO
  userId: number;
  username: string;
  partnerName: string;
  partnerCode: string;
  mobile: string | null;
  email: string | null;
}

export interface IssueReply {
  id: number;
  body: string;
  createdAt: string | null; // UTC ISO
  createdBy: string | null;
  /** true = the partner's follow-up message; false = an admin reply. */
  fromPartner: boolean;
}

/** `data` of GET /IssueThread. Opening the thread marks the issue read for the admin. */
export interface IssueThread {
  issue: IssueDetail;
  replies: IssueReply[];
}

/** Write model for POST /Issues/Reply. The reply shows up in the partner's app. */
export interface IssueReplyDto {
  issueId: number;
  body: string;
}

// ===================== defensive mappers =====================
// Dapper-dynamic endpoints serialize PascalCase; typed DTOs serialize camelCase.
// Read both so grids never render blank (see CLAUDE.md).

function pick<T = any>(x: any, ...keys: string[]): T | undefined {
  for (const k of keys) if (x?.[k] != null) return x[k] as T;
  return undefined;
}
function asBool(v: any, dflt = false): boolean {
  if (v == null) return dflt;
  return v === true || v === 'true' || v === 1 || v === '1';
}
function asNum(v: any, dflt = 0): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : dflt;
}
function pickNum(x: any, ...keys: string[]): number | null {
  const v = pick(x, ...keys);
  if (v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/** UTC ISO string that may lack the trailing 'Z' → always-UTC ISO so `indDate` renders local time. */
function utc(s: any): string | null {
  if (typeof s !== 'string' || !s) return null;
  return /([zZ]|[+-]\d{2}:?\d{2})$/.test(s) ? s : s + 'Z';
}

function mapUser(x: any): PartnerAppUser {
  return {
    userId: asNum(pick(x, 'UserId', 'userId')),
    username: pick(x, 'Username', 'username') ?? '',
    partnerName: pick(x, 'PartnerName', 'partnerName') ?? '',
    partnerCode: pick(x, 'PartnerCode', 'partnerCode') ?? '',
  };
}

function mapPush(x: any): PushRow {
  return {
    id: asNum(pick(x, 'Id', 'id')),
    title: pick(x, 'Title', 'title') ?? '',
    body: pick(x, 'Body', 'body') ?? '',
    createdAt: utc(pick(x, 'CreatedAt', 'createdAt')),
    userId: pickNum(x, 'UserId', 'userId'),
    targetUsername: pick(x, 'TargetUsername', 'targetUsername') ?? null,
    readCount: asNum(pick(x, 'ReadCount', 'readCount')),
  };
}

function mapIssue(x: any): IssueRow {
  return {
    id: asNum(pick(x, 'Id', 'id')),
    title: pick(x, 'Title', 'title') ?? '',
    body: pick(x, 'Body', 'body') ?? '',
    status: (pick(x, 'Status', 'status') ?? '').toUpperCase(),
    createdAt: utc(pick(x, 'CreatedAt', 'createdAt')),
    isReadByAdmin: asBool(pick(x, 'IsReadByAdmin', 'isReadByAdmin')),
    userId: asNum(pick(x, 'UserId', 'userId')),
    username: pick(x, 'Username', 'username') ?? '',
    partnerName: pick(x, 'PartnerName', 'partnerName') ?? '',
    partnerCode: pick(x, 'PartnerCode', 'partnerCode') ?? '',
    replyCount: asNum(pick(x, 'ReplyCount', 'replyCount')),
  };
}

function mapIssueDetail(x: any): IssueDetail {
  return {
    id: asNum(pick(x, 'Id', 'id')),
    title: pick(x, 'Title', 'title') ?? '',
    body: pick(x, 'Body', 'body') ?? '',
    status: (pick(x, 'Status', 'status') ?? '').toUpperCase(),
    createdAt: utc(pick(x, 'CreatedAt', 'createdAt')),
    userId: asNum(pick(x, 'UserId', 'userId')),
    username: pick(x, 'Username', 'username') ?? '',
    partnerName: pick(x, 'PartnerName', 'partnerName') ?? '',
    partnerCode: pick(x, 'PartnerCode', 'partnerCode') ?? '',
    mobile: pick(x, 'Mobile', 'mobile') ?? null,
    email: pick(x, 'Email', 'email') ?? null,
  };
}

function mapReply(x: any): IssueReply {
  return {
    id: asNum(pick(x, 'Id', 'id')),
    body: pick(x, 'Body', 'body') ?? '',
    createdAt: utc(pick(x, 'CreatedAt', 'createdAt')),
    createdBy: pick(x, 'CreatedBy', 'createdBy') ?? null,
    fromPartner: !!pick(x, 'FromPartner', 'fromPartner'),
  };
}

function mapThread(x: any): IssueThread {
  const issue = pick(x, 'Issue', 'issue') ?? {};
  const replies = pick<any[]>(x, 'Replies', 'replies');
  return {
    issue: mapIssueDetail(issue),
    replies: (Array.isArray(replies) ? replies : []).map(mapReply),
  };
}

function mapEnv<T>(fn: (x: any) => T) {
  return (res: any) => {
    if (res && Array.isArray(res.data)) return { ...res, data: res.data.map(fn) };
    if (res && res.data && typeof res.data === 'object') return { ...res, data: fn(res.data) };
    return res;
  };
}

// ===================== service =====================

/**
 * Admin side of the partner mobile app's notifications: push messages to one
 * partner or broadcast to all, and read/reply/close partner-reported issues
 * (replies appear inside the partner's app). Backend is admin-JWT gated.
 * All `createdAt` values are UTC ISO strings.
 */
@Injectable({ providedIn: 'root' })
export class PartnerAppAdminService {
  private http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiUrl}/PartnerAppAdmin`;

  /** Partner app users — the push-target dropdown source. */
  partnerUsers() {
    return this.http
      .get<ApiEnvelope<PartnerAppUser[]>>(`${this.baseUrl}/PartnerUsers`)
      .pipe(map(mapEnv(mapUser)));
  }

  /** Send a push. `userId` null = broadcast to ALL partner app users. */
  push(dto: PushNotificationDto) {
    return this.http.post<ApiEnvelope>(`${this.baseUrl}/Push`, dto);
  }

  /** Sent-push history, newest first. */
  pushes(take = 100) {
    return this.http
      .get<ApiEnvelope<PushRow[]>>(`${this.baseUrl}/Pushes`, { params: { take } })
      .pipe(map(mapEnv(mapPush)));
  }

  /** Partner-reported issues, optionally filtered by status and/or a search term. */
  issues(status?: string | null, search?: string | null) {
    let params = new HttpParams();
    if (status) params = params.set('status', status);
    if (search) params = params.set('search', search);
    return this.http
      .get<ApiEnvelope<IssueRow[]>>(`${this.baseUrl}/Issues`, { params })
      .pipe(map(mapEnv(mapIssue)));
  }

  /** Full thread of one issue. Opening it marks the issue read for the admin (server-side). */
  issueThread(issueId: number) {
    return this.http
      .get<ApiEnvelope<IssueThread>>(`${this.baseUrl}/IssueThread`, { params: { issueId } })
      .pipe(map(mapEnv(mapThread)));
  }

  /** Reply to an issue — the partner sees the reply in the app. */
  reply(dto: IssueReplyDto) {
    return this.http.post<ApiEnvelope>(`${this.baseUrl}/Issues/Reply`, dto);
  }

  /** Mark an issue CLOSED. The thread stays readable afterwards. */
  closeIssue(issueId: number) {
    return this.http.post<ApiEnvelope>(`${this.baseUrl}/Issues/Close`, {}, { params: { issueId } });
  }
}
